import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addFavoriteProduct,
  getFavoriteStatus,
  removeFavoriteProduct,
} from '../api/endpoints';
import {
  postFavoriteAdd,
  postFavoriteRemove,
} from '../api/telemetry';

export type UseFavoriteProductOptions = {
  productId?: number | null;
  initial?: boolean;
  disabled?: boolean;
};

export function useFavoriteProduct({
  productId,
  initial,
  disabled,
}: UseFavoriteProductOptions) {
  const [favorited, setFavorited] = useState<boolean | null>(typeof initial === 'boolean' ? initial : null);
  const [loading, setLoading] = useState(false);
  const id = useMemo(() => (typeof productId === 'number' && Number.isFinite(productId) ? productId : null), [productId]);

  useEffect(() => {
    if (typeof initial === 'boolean') {
      setFavorited(initial);
    }
  }, [initial]);

  useEffect(() => {
    if (!id || disabled) return;
    let alive = true;
    getFavoriteStatus(id)
      .then((res) => {
        if (!alive) return;
        if (typeof res.favorited === 'boolean') {
          setFavorited(res.favorited);
        }
      })
      .catch(() => {
        if (alive) setFavorited(null);
      });
    return () => {
      alive = false;
    };
  }, [id, disabled]);

  const toggle = useCallback(async () => {
    if (!id || loading || disabled) return;
    setLoading(true);
    try {
      if (favorited) {
        await removeFavoriteProduct(id);
        setFavorited(false);
        // Track removal telemetry
        void postFavoriteRemove(id).catch(() => { /* telemetry non-fatal */ });
      } else {
        await addFavoriteProduct(id);
        setFavorited(true);
        // Track addition telemetry
        void postFavoriteAdd(id).catch(() => { /* telemetry non-fatal */ });
      }
    } finally {
      setLoading(false);
    }
  }, [favorited, id, loading, disabled]);

  return { favorited, loading, toggle, canToggle: !!id && !disabled };
}

export default useFavoriteProduct;
